import {cellElements, mapTableCellElements} from './cell-elements';
import type {CellElement} from './table-types';
import type {PhotoRequirement} from './types';

/**
 * The element variants that carry a stamped org-library logo.
 *
 * Narrowing on the discriminant rather than casting to a hand-written
 * `{logo_id?, logo_url?}` shape: the union already says which variants have the
 * field, so a cast could only ever hide a variant that doesn't.
 */
type LogoBearing = Extract<PhotoRequirement, {type: 'photo' | 'logo'}>;

function isLogoBearing(req: PhotoRequirement): req is LogoBearing {
  return req.type === 'photo' || req.type === 'logo';
}

/**
 * The `logo_id` on a cell element, if it carries one.
 *
 * Both cell types that stamp a library photo answer here now. DERP-225 added
 * the id for cell PHOTOS only, which left a cell `logo` holding a bare
 * `logo_url` that nothing could refresh — a presigned URL that expired in
 * minutes, or a base64 data URL written into the template by an upload that was
 * never staged. Narrowing on the discriminant rather than casting to a
 * hand-written `{logo_id?}` shape, so a third variant that gains the field is a
 * compile error here rather than a lookup that silently never matches.
 */
function cellLogoIdOf(el: CellElement): string | undefined {
  return el.type === 'photo' || el.type === 'logo' ? el.config.logo_id : undefined;
}

/** The currently-cached URL for `cellLogoIdOf`'s logo. */
function cellLogoUrlOf(el: CellElement): string | undefined {
  return el.type === 'photo' || el.type === 'logo' ? el.config.logo_url : undefined;
}

/**
 * Every org-library logo this layout references, standalone elements and table
 * cells alike.
 *
 * A generator so the two questions asked of it stay one walk: "does this
 * template stamp any logo at all" bails on the first id, and "which ones" takes
 * them all. They were two separate traversals with two separate ideas of where
 * a `logo_id` can hide, which is how a cell logo came to be missed once already.
 *
 * Ids repeat freely — one org logo is usually stamped on every slot — so a
 * caller that wants them unique says so (`logoIdsIn`).
 */
function* logoIds(requirements: readonly PhotoRequirement[]): Generator<string> {
  for (const r of requirements) {
    if (isLogoBearing(r) && r.config.logo_id) {
      yield r.config.logo_id;
      continue;
    }
    if (r.type !== 'table') continue;
    for (const {el} of cellElements(r.config)) {
      const id = cellLogoIdOf(el);
      if (id) yield id;
    }
  }
}

/** True when at least one requirement — or any table cell — references a logo. */
export function templateHasLogoId(requirements: PhotoRequirement[]): boolean {
  return !logoIds(requirements).next().done;
}

/**
 * The distinct logos a layout needs URLs for — what `rehydrateLogoUrls`'s map
 * has to cover, and therefore what a caller has to go and fetch.
 *
 * Stated as the ids rather than left implicit in "the photo library", because
 * the library is paginated and the two are not the same set: reading one page of
 * it and hoping the ids are on it is exactly the bug this replaced.
 */
export function logoIdsIn(requirements: readonly PhotoRequirement[]): Set<string> {
  return new Set(logoIds(requirements));
}

/**
 * Refresh the presigned `logo_url` on every requirement carrying a `logo_id`,
 * from a fresh id→url map built off the org photo library.
 *
 * Presigned S3 download URLs expire (15 min by default), so the URL stored in a
 * saved template goes stale — the logo then silently fails to load. This must
 * run on every template open. It covers BOTH the dedicated `logo` element and
 * the image/photo elements that stamp a logo via `show_logo` + `logo_id`
 * (originally only the former was refreshed, so image-input logos vanished).
 *
 * **An id the map does not answer for loses its URL.** It used to keep it, and
 * that stored URL is a cache of an id that no longer resolves — a presigned link
 * to an object that is gone. Keeping it meant `renderers/logo.tsx` drew a broken
 * image on every surface (editor, report, PDF) with nothing to say why, and no
 * control could tell "this logo is missing" from "this logo has a URL", because
 * on the element they looked identical. Cleared, the absence IS the answer:
 * `logo_id && !logo_url` means gone, everywhere, with no second lookup.
 *
 * **Only ever call this with a map you actually got.** The caller's `try/catch`
 * is what makes "absent from the map" mean "deleted" rather than "we could not
 * ask" — a failed fetch must leave the requirements alone, or a flaky connection
 * strips every good logo in the template. That is the whole precondition.
 *
 * Requirements without a `logo_id` are returned unchanged, by reference.
 */
export function rehydrateLogoUrls(
  requirements: PhotoRequirement[],
  urlById: ReadonlyMap<string, string>,
): PhotoRequirement[] {
  return mapLogoUrls(requirements, (logoId) => urlById.get(logoId));
}

/** The stand-in every stamped logo gets in a compare-safe copy. Any constant
 *  does; it reads as what it is in a failing diff. */
const PINNED_LOGO_URL = 'logo:pinned-for-comparison';

/**
 * A copy in which every stamped logo's `logo_url` is the same constant — so two
 * layouts compare equal whenever their logos are the same logos, regardless of
 * which presigned URL each copy happened to be minted with.
 *
 * **The fields that churn are exactly the fields `rehydrateLogoUrls` refreshes**,
 * which is why this reuses its walk rather than carrying its own idea of where
 * a volatile URL can hide. A `logo_url` reachable from a `logo_id` is a cache of
 * that id and is re-minted on every read; one with no id behind it is a stored
 * string nothing refreshes (see `cellLogoIdOf`'s known gap), so it is stable and
 * comparing it is correct. Pinning by id therefore neutralises the volatile
 * fields and only those.
 */
export function pinLogoUrls(requirements: PhotoRequirement[]): PhotoRequirement[] {
  return mapLogoUrls(requirements, () => PINNED_LOGO_URL);
}

/**
 * A logo config carrying whatever the lookup said: the fresh URL, or NO
 * `logo_url` key at all when the id did not resolve.
 *
 * The key is DELETED rather than set to `undefined`, because `deepEqual`
 * compares key counts — `{logo_url: undefined}` and `{}` are not equal to it, so
 * an undefined would read as an edit and make a template dirty just for being
 * opened.
 */
function withLogoUrl<T extends {logo_url?: string}>(cfg: T, freshUrl: string | undefined): T {
  if (freshUrl) return {...cfg, logo_url: freshUrl};
  const next = {...cfg};
  delete next.logo_url;
  return next;
}

/** The shared walk: rewrite each stamped logo's URL to whatever `freshUrlFor`
 *  gives for its id, standalone elements and table cells alike. Elements whose
 *  URL is already the right one — including already-cleared ones, where both
 *  sides are `undefined` — come back by reference, which is what keeps the
 *  memoization downstream intact. */
function mapLogoUrls(
  requirements: PhotoRequirement[],
  freshUrlFor: (logoId: string) => string | undefined,
): PhotoRequirement[] {
  return requirements.map((req) => {
    // A table carries its logos INSIDE its cells, so its own config has no
    // logo_id to find — recurse or a cell photo's stamped logo silently stops
    // loading once the presigned URL expires.
    if (req.type === 'table') return rehydrateTableLogos(req, freshUrlFor);
    if (!isLogoBearing(req) || !req.config.logo_id) return req;
    const freshUrl = freshUrlFor(req.config.logo_id);
    if (freshUrl === req.config.logo_url) return req;
    return {...req, config: withLogoUrl(req.config, freshUrl)};
  });
}

/**
 * Refresh every cell element's `logo_url` inside one table.
 *
 * `mapTableCellElements` owns the walk and the identity-preservation; all that
 * is left here is what a stale element becomes. The table comes back unchanged
 * by reference when nothing was stale, which is the overwhelmingly common case
 * and what keeps the memoization downstream of `rawRequirements` intact.
 */
function rehydrateTableLogos(
  table: Extract<PhotoRequirement, {type: 'table'}>,
  freshUrlFor: (logoId: string) => string | undefined,
): PhotoRequirement {
  const config = mapTableCellElements(table.config, (el) => {
    // The two types `cellLogoIdOf` answers for, named again rather than cast:
    // narrowing on the discriminant is what rebuilds each config, and keeps the
    // two lists in step by compile error.
    if (el.type !== 'photo' && el.type !== 'logo') return el;
    const logoId = cellLogoIdOf(el);
    if (!logoId) return el;
    // The lookup's answer, whatever it is — a URL to write, or `undefined`
    // meaning the id is gone and the stored URL has to go with it. Comparing to
    // what is there covers both "already current" and "already cleared" in one,
    // where the old code conflated "gone" with "unchanged" and so could never
    // clear anything.
    const freshUrl = freshUrlFor(logoId);
    if (freshUrl === cellLogoUrlOf(el)) return el;
    return {...el, config: withLogoUrl(el.config, freshUrl)};
  });
  return config === table.config ? table : {...table, config};
}
